import React, { useEffect } from 'react';
import { ArrowUp, Delete } from 'lucide-react';

interface PredictiveKeyboardProps {
  currentInput: string;
  suggestions: string[];
  isLoadingSuggestions: boolean;
  onSelectSuggestion: (word: string) => void;
  onSend: () => void;
  onBackspace: () => void;
  disabled: boolean;
  isDarkMode?: boolean;
}

export const PredictiveKeyboard: React.FC<PredictiveKeyboardProps> = ({
  currentInput,
  suggestions,
  isLoadingSuggestions,
  onSelectSuggestion,
  onSend,
  onBackspace,
  disabled,
  isDarkMode
}) => {
  
  // Safe defaults if empty
  const displaySuggestions = suggestions.length === 3 ? suggestions : ["...", "...", "..."];

  return (
    <div className={`border-t pb-safe pt-2 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-200 border-gray-300'}`}>
      {/* Input Preview Area */}
      <div className="px-4 py-2 mb-2">
        <div className={`rounded-xl px-4 py-3 min-h-[50px] flex items-center justify-between shadow-inner border ${
          isDarkMode 
            ? 'bg-gray-700 border-gray-600' 
            : 'bg-white border-gray-300'
        }`}>
          <span className={`text-lg ${currentInput ? (isDarkMode ? 'text-gray-100' : 'text-gray-900') : (isDarkMode ? 'text-gray-500 italic' : 'text-gray-400 italic')}`}>
            {currentInput || "Tap a suggestion..."}
          </span>
          
          <div className="flex items-center gap-2">
             {/* Backspace Button */}
            <button
                onClick={onBackspace}
                disabled={disabled || !currentInput}
                className={`p-2 rounded-full transition-colors ${
                !currentInput || disabled 
                  ? (isDarkMode ? 'text-gray-600' : 'text-gray-300')
                  : isDarkMode 
                    ? 'text-gray-400 hover:bg-gray-600 active:bg-gray-500' 
                    : 'text-gray-500 hover:bg-gray-100 active:bg-gray-200'
                }`}
            >
                <Delete size={20} />
            </button>

            {/* Send Button - iOS Style */}
            <button
                onClick={onSend}
                disabled={disabled || !currentInput}
                className={`w-8 h-8 rounded-full transition-all duration-200 flex items-center justify-center ${
                !currentInput || disabled
                    ? (isDarkMode ? 'bg-gray-600 text-gray-500 cursor-not-allowed' : 'bg-gray-300 text-gray-500 cursor-not-allowed')
                    : 'bg-blue-500 text-white shadow-lg hover:bg-blue-600 active:scale-90'
                }`}
            >
                <ArrowUp size={18} strokeWidth={2.5} />
            </button>
          </div>
        </div>
      </div>

      {/* Predictive Bar */}
      <div className="grid grid-cols-3 gap-2 px-2 pb-4 h-16">
        {displaySuggestions.map((suggestion, idx) => (
          <button
            key={`${suggestion}-${idx}`}
            onClick={() => onSelectSuggestion(suggestion)}
            disabled={disabled || isLoadingSuggestions}
            className={`
              relative overflow-hidden
              h-full rounded-lg text-sm font-medium leading-tight px-1
              flex items-center justify-center text-center
              transition-all duration-150 active:scale-95
              ${disabled 
                ? isDarkMode 
                  ? 'bg-gray-600 text-gray-500 opacity-50' 
                  : 'bg-gray-300 text-gray-500 opacity-50'
                : isDarkMode
                  ? 'bg-gray-700 text-gray-200 shadow-sm border-b-2 border-gray-600 hover:bg-gray-600'
                  : 'bg-white text-gray-800 shadow-sm border-b-2 border-gray-300 hover:bg-gray-50'
              }
            `}
          >
            {isLoadingSuggestions ? (
               <div className={`w-4 h-4 border-2 border-t-transparent rounded-full animate-spin ${isDarkMode ? 'border-gray-400' : 'border-gray-400'}`}></div>
            ) : (
                <span>{suggestion}</span>
            )}
             {/* Key Highlight Effect */}
             {!disabled && !isLoadingSuggestions && (
                <div className="absolute inset-0 bg-black opacity-0 hover:opacity-5 transition-opacity" />
             )}
          </button>
        ))}
      </div>
      
      {/* Fake Keyboard Visuals (Decoration) */}
      <div className="px-1 pb-2">
         <div className="flex justify-center gap-1 mb-1 opacity-30 pointer-events-none select-none">
            {['Q','W','E','R','T','Y','U','I','O','P'].map(k => (
                <div key={k} className="w-8 h-10 bg-gray-400 rounded flex items-center justify-center text-white text-xs font-bold">{k}</div>
            ))}
         </div>
         <div className="flex justify-center gap-1 mb-1 opacity-30 pointer-events-none select-none">
            {['A','S','D','F','G','H','J','K','L'].map(k => (
                <div key={k} className="w-8 h-10 bg-gray-400 rounded flex items-center justify-center text-white text-xs font-bold">{k}</div>
            ))}
         </div>
         <div className="flex justify-center gap-1 opacity-30 pointer-events-none select-none">
            <div className="w-10 h-10 bg-gray-400 rounded flex items-center justify-center text-white text-xs font-bold">⇧</div>
            {['Z','X','C','V','B','N','M'].map(k => (
                <div key={k} className="w-8 h-10 bg-gray-400 rounded flex items-center justify-center text-white text-xs font-bold">{k}</div>
            ))}
            <div className="w-10 h-10 bg-gray-400 rounded flex items-center justify-center text-white text-xs font-bold">⌫</div>
         </div>
         <div className="flex justify-center mt-2 opacity-30 pointer-events-none">
             <div className="w-40 h-8 bg-gray-400 rounded-full"></div>
         </div>
      </div>
    </div>
  );
};
